import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import VideoService from '../../../service/VideoService';
import { useUserContext } from '../../../context/UserContext';
import styles from './UsagePage.module.scss';
import { CustomButton } from '../../../components/Button/Button';
import { ToastNotification } from '../../../components/Toast/Toast';
import UsageUsersList from './components/UsageUsersList/UsageUsersList';
import UsageLoader from './components/UsageLoader/UsageLoader';
import UsageModal from './components/UsageModal/UsageModal';
import UsageList from './components/UsageList/UsageList';

/**
 * @callback companyUsageCallback
 * @param {CompanyUsage} usage
 */

/**
 * @param {number} companyId
 */
const getSortedUsages = async companyId => {
  const usages = await VideoService.getCompanyUsages(companyId);
  usages.sort((a, b) => {
    return new Date(a.startDate) > new Date(b.startDate) ? -1 : 0;
  });
  return usages;
};

/**
 * @param {number} companyId
 * @return {JSX.Element}
 * @constructor
 */
const UsagePage = ({ companyId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [companyUsages, setCompanyUsages] = useState(/** @type {CompanyUsage[]} */ []);
  const { isSuperAdmin } = useUserContext();
  const { t } = useTranslation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);

  const onButtonClick = useCallback(() => {
    setUsage(null);
    setIsModalOpen(true);
  }, []);

  const onCloseClick = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleEdit = useCallback(usageItem => {
    setUsage(usageItem);
    setIsModalOpen(true);
  }, []);

  const usageId = usage?.id;

  const onConfirmClick = useCallback(
    async payload => {
      setIsLoading(true);
      try {
        if (usageId) {
          await VideoService.updateCompanyUsage(usageId, payload);
        } else {
          await VideoService.createCompanyUsage(companyId, payload);
        }
        ToastNotification({
          message: t('company.settings.usage.toast.modify.success'),
          iconName: 'success',
        });
        try {
          const usages = await getSortedUsages(companyId);
          setCompanyUsages(usages);
        } catch (e) {
          ToastNotification({
            message: t('company.settings.usage.toast.list.error'),
          });
        }
      } catch (e) {
        const errorMessage = e.response?.data?.message;
        ToastNotification({
          message: `${t('company.settings.usage.toast.modify.error')} ${errorMessage}`,
        });
      }
      setUsage(null);
      setIsModalOpen(false);
      setIsLoading(false);
    },
    [t, usageId, companyId]
  );

  useEffect(() => {
    let ignore = false;

    async function fetchCompanyUsages() {
      setIsLoading(true);
      try {
        const usages = await getSortedUsages(companyId);
        if (!ignore) {
          if (usages.length) {
            setSelectedContract(usages[0]);
          }
          setCompanyUsages(usages);
        }
      } catch (e) {
        ToastNotification({
          message: t('company.settings.usage.toast.list.error'),
        });
      }
      setIsLoading(false);
    }

    if (companyId) {
      setSelectedContract(null);
      fetchCompanyUsages();
    } else {
      setCompanyUsages([]);
      setSelectedContract(null);
      setIsLoading(false);
    }
    return () => {
      ignore = true;
    };
  }, [companyId, t]);

  if (isLoading) {
    return <UsageLoader />;
  } else if (isSuperAdmin) {
    return (
      <div className={!companyUsages.length ? styles.usageCenterState : ''}>
        <UsageList
          usages={companyUsages}
          isSuperAdmin={isSuperAdmin}
          handleEdit={handleEdit}
          handleSelectUsage={setSelectedContract}
        />
        <CustomButton
          label={t('company.settings.usage.add')}
          variant="primary"
          onClick={onButtonClick}
          className={styles.usageButton}
        />
        {selectedContract && <UsageUsersList companyId={companyId} contract={selectedContract} />}
        <UsageModal
          usage={usage}
          isOpen={isModalOpen}
          isLoading={isLoading}
          onCloseClick={onCloseClick}
          onConfirmClick={onConfirmClick}
        />
      </div>
    );
  } else {
    return (
      <>
        <UsageList
          usages={companyUsages}
          isSuperAdmin={isSuperAdmin}
          handleEdit={handleEdit}
          handleSelectUsage={setSelectedContract}
        />
        {selectedContract && <UsageUsersList companyId={companyId} contract={selectedContract} />}
      </>
    );
  }
};

export default UsagePage;
